import {
  Listing,
  type ListingFilterValues,
  useListing,
} from "@verde/compose-ui";
import { useFetchCreditUsages } from "@verde/entities";
import { ExpandIcon } from "@verde/icons";
import { Copyable, IconButton } from "@verde/ui-core";
import { dayjs, formatDate, formatToCPF } from "@verde/utils";
import React, {
  type DetailedHTMLProps,
  type HTMLAttributes,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

export const styles = tv({
  slots: {
    base: "inline-flex items-center gap-1",
    arrow: "absolute top-1/2 -right-4 -translate-y-1/2",
  },
  variants: {
    sortable: {
      true: {
        base: "cursor-pointer select-none",
      },
    },
  },
});

function CellHead({
  title,
  sort,
  params,
}: { title: string; sort: string; params: ListingFilterValues }) {
  const sortable = useMemo(() => !!sort, [sort]);
  const { form } = useListing();

  const value = useMemo(() => {
    if (!params?.sort || !sort) return;
    return params.sort?.split(",").find((item) => item.includes(sort));
  }, [params, sort]);

  const newValue = useMemo(() => {
    if (!sort) return;

    if (!value) {
      return `${sort}:desc`;
    }

    if (value === `${sort}:desc`) {
      return `${sort}:asc`;
    }

    return undefined;
  }, [sort, value]);

  const handleValue = useCallback(() => {
    form.setValue("sort", newValue);
  }, [form, newValue]);

  const makeContent = useCallback(
    (
      props?: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    ) => {
      return (
        <div
          className={styles().base({
            sortable,
          })}
          {...(sortable && {
            role: "button",
          })}
          {...props}
        >
          <p className="line-clamp-1 font-semibold">{title}</p>
        </div>
      );
    },
    [sortable, title],
  );

  return (
    <div className="relative inline-flex">
      {makeContent({
        onClick: () => {
          handleValue();
        },
      })}

      {value && (
        <span className={styles().arrow()}>
          {
            {
              [`${sort}:asc`]: <>&#9650;</>,
              [`${sort}:desc`]: <>&#9660;</>,
            }[value]
          }
        </span>
      )}
    </div>
  );
}

function PanelSearchClientHistoryListing() {
  const navigate = useNavigate();

  const [filterParams, setFilterParams] = useState<ListingFilterValues>({
    sort: "created_at:desc",
  });

  const { data, isLoading } = useFetchCreditUsages(filterParams);

  return (
    <Listing
      onFilterChange={setFilterParams}
      classNames={{ base: "p-0" }}
      table={{
        loading: isLoading,
        header: {
          name: {
            render: (
              <CellHead params={filterParams} sort="name" title="Produtor(a)" />
            ),
            styles: { body: "sm:pr-0", head: "sm:pr-0" },
          },
          created_at: {
            render: (
              <CellHead
                params={filterParams}
                sort="created_at"
                title="Último Raio-X"
              />
            ),
            styles: { body: "sm:pl-6", head: "sm:pl-6" },
          },
          actions: { styles: { body: "sm:pl-0", head: "sm:pl-0" } },
        },
        data: data?.data.map(({ id, client, created_at }) => {
          return {
            name: (
              <div className="grid gap-1">
                <p className="font-semibold">{client.name}</p>
                <p className="text-sm font-medium">
                  <Copyable>{formatToCPF(client.document_number)}</Copyable>
                </p>
              </div>
            ),
            created_at: (
              <div className="grid w-20 gap-1">
                <p className="font-medium">{formatDate(created_at)}</p>
                <p className="text-sm font-medium">
                  {dayjs(created_at).format("HH:mm")}
                </p>
              </div>
            ),
            actions: (
              <IconButton
                kind="secondary"
                onClick={() => {
                  navigate(`/raio-x-rural/${client.id}/${id}`);
                }}
              >
                <ExpandIcon />
              </IconButton>
            ),
          };
        }),
      }}
    />
  );
}

export default React.memo(PanelSearchClientHistoryListing);
